// @flow
import React, { Component } from "react";
import { css } from "glamor";
import cx from "classnames";
import glamorous from "glamorous";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import tippy from "tippy.js";
import { calculateCrop } from "utils/cropUtils/cropMath";
import t from "common/i18n";
import "./CropWidget.scss";

type Props = {
	cropSource: Object,
	options: Object,
	setCrop: Function,
	setDefaultCrop: Function,
	onError: Function,
	crop: Object,
};

type State = {
	style: Object,
	hideHandlesWestEast: boolean,
	hideHandlesNorthSouth: boolean,
	hideHandlesCorners: boolean,
}

// Size thresholds (%)
const HIDE_HORIZONTAL_HANDLES_THRESHOLD = 20;
const HIDE_VERTICAL_HANDLES_THRESHOLD = 17;
const HIDE_CORNER_HANDLES_THRESHOLD = 11;
const DIMENSIONS_HORIZONTAL_THRESHOLD = 13;
const DIMENSIONS_VERTICAL_THRESHOLD = 15;

const CROP_AREA_HEIGHT = 478;
const CROP_AREA_WIDTH = 767;
const CROP_AREA_RATIO = CROP_AREA_WIDTH / CROP_AREA_HEIGHT;

// Crop dimensions (px) indicator
const GDimensions = glamorous.div(
	"bg-window-c bg-window",
	"absolute o-70 br1 pv2 ph3 f6 nowrap",
	(props) => {
		const cropIsSmall = props.crop.height < DIMENSIONS_HORIZONTAL_THRESHOLD || props.crop.width < DIMENSIONS_VERTICAL_THRESHOLD;
		const cropBottom = props.crop.y + props.crop.height;
		return {
			top:
			// if short (height) and narrow (width) and has place below - position below
				cropIsSmall && cropBottom < 90
					? `${cropBottom + 10}%`
					// if short (height) and narrow (width) and no place below - position above
					: cropIsSmall && cropBottom > 90
						? `${props.crop.y}%`
					// else - position inside
						: `${cropBottom || 100}%`,
			left: `${props.crop.x + props.crop.width / 2 || 50}%`,
			transform: "translate(-50%, -35px)",
			pointerEvents: "none",
		};
	},
);

const cropStyles = `
    .ReactCrop, .ReactCrop__image {
        max-height: 100%;
    }
`;
css.insert(cropStyles);

class CropWidget extends Component<Props, State> {
	static defaultProps = {
		cropSource: {},
		options: {},
	};

	state = {
		hideHandlesWestEast: false,
		hideHandlesNorthSouth: false,
		hideHandlesCorners: false,
		style: {
			height: "100%", // fixes safari bug with portrait images pushed to the left
		},
	};

	componentWillUnmount() {
		if (document.body) {
			document.body.removeEventListener("mouseup", this.enableAllTips, true);
		}
	}

	onChange = (crop: Object, pixelCrop: Object) => {
		if (crop.width && crop.height) {
			this.props.setCrop({ ...crop, pixelCrop });
		}

		this.setState({
			hideHandlesWestEast: (crop.height < HIDE_VERTICAL_HANDLES_THRESHOLD),
			hideHandlesNorthSouth: (crop.width < HIDE_HORIZONTAL_HANDLES_THRESHOLD),
			hideHandlesCorners: (crop.width < HIDE_CORNER_HANDLES_THRESHOLD || crop.height < HIDE_CORNER_HANDLES_THRESHOLD),
		});
	};

	onImageLoaded = (image: Object) => {
		setTimeout(() => { // firefox needs more time because image size can be 0 even though loaded
			if (this.widgetRef.current) {
				const { componentRef } = this.widgetRef.current;
				const cropWidgetHeight = componentRef.parentElement.clientHeight;
				const imageRatio = image.width / image.height;
				// This is needed to make images that are higher than the widget
				// respect the max-height property, whithout breaking centering
				// of smaller images (prevent this - https://codepen.io/tsi/pen/rvxeNE)

				if (cropWidgetHeight) {
					// Crop already rendered
					if (image.clientHeight < cropWidgetHeight) {
						this.setState({ style: { height: "auto" } });
					}
				} else if (imageRatio > CROP_AREA_RATIO || image.height < CROP_AREA_HEIGHT) {
					// Crop widget not rendered yet but image is not a big portrait image.
					this.setState({ style: { height: "auto" } });
					//TODO - check logic here - is last else-if never reached???
					// eslint-disable-next-line  no-dupe-else-if
				} else if (imageRatio < 1 && cropWidgetHeight && image.height > cropWidgetHeight) {
					// force 100% in case portrait image with height taller than widget height
					this.setState({ style: { height: "100%" } });
				}

				//turn HTMLCollection into an array of elements
				this.handleList = Array.prototype.slice.call(componentRef.getElementsByClassName("ReactCrop__drag-handle"));

				if (document.body) {
					document.body.addEventListener("mouseup", this.enableAllTips, true);
					this.setTooltips();
				}
			}

			// Set initial crop.
			// Normally, we could do this earlier but fixed-ratio crop defaults can be
			// calculated only after we know image proportions.
			const cropCalc = calculateCrop(this.props.options, image);

			this.props.setDefaultCrop({ ...cropCalc });

			this.props.setCrop({
				...cropCalc,
				dimensions: [image.naturalWidth, image.naturalHeight]
			});

			// IE 11 hack
			// Set image width explicitly
			image.style.width = `${image.width}px`;
		}, 100);
	};

	setTooltips = () => {
		if (this.handleList) {
			this.handleList.forEach((handle) => {
				handle.setAttribute("data-tippy-content", handle.dataset.ord.length > 1
					? t("crop.corner_tooltip")
					: t("crop.handle_tooltip"));
				tippy(handle, {
					animateFill: false,
					animation: "shift-toward",
					delay: [500, 100],
				});
				handle.addEventListener("mousedown", () => {
					const instance = handle._tippy;
					instance.hide();
					instance.disable();
				});
			});
		}
	};

	enableAllTips = () => {
		if (this.handleList) {
			this.handleList.forEach((handle) => {
				if (handle._tippy) {
					handle._tippy.enable();
				}
			});
		}
	};

	handleList = null;

	widgetRef = React.createRef<Object>();

	cropWidget: Object;

	dimensions: Object;

	render() {
		const { cropSource, crop, options } = this.props;
		const { hideHandlesWestEast, hideHandlesNorthSouth, hideHandlesCorners } = this.state;
		const src = cropSource.src || "";
		return src ? (
			<ReactCrop
				{...this.state}
				outputPixelUnits
				crop={crop}
				src={src}
				minWidth={crop.minWidth || undefined}
				minHeight={crop.minHeight || undefined}
				onChange={this.onChange}
				onImageLoaded={this.onImageLoaded}
				onImageError={this.props.onError}
				ref={this.widgetRef}
				className={cx({ hideHandlesWestEast, hideHandlesNorthSouth, hideHandlesCorners })}
				keepSelection={window.innerWidth < CROP_AREA_WIDTH}
			>
				{options.croppingShowDimensions && crop.pixelCrop
				&& <GDimensions crop={crop}>
					{`${parseInt(crop.pixelCrop.width)} x ${parseInt(crop.pixelCrop.height)} px`}
				</GDimensions>}
			</ReactCrop>
		) : null;
	}
}

export default CropWidget;
